
    // IMPORTS
    import { computed, defineComponent, PropType, Component, ref, h } from 'vue'

    import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-vue-inputs'
    import { BoolField, DateField } from '../fields'

    import { guid } from '@progress/kendo-vue-common'
    import { GridCellProps, GridColumnProps } from '@progress/kendo-vue-grid';

    export default defineComponent({
        name: 'gridDefaultRenderCell',

        components: {
            'k-checkbox': Checkbox,
            BoolField,
            DateField
        },

        inheritAttrs: false,

        props: {
            cellProps: { type: Object as PropType<GridCellProps>, required: true },
            columns: {type: Object as PropType<GridColumnProps[]>, required: true}
        },

        emits: {             
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            click: (event: MouseEvent, dataItem: any, expanded?: boolean ) => true,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            selectionchange: (data: {event: CheckboxChangeEvent, dataItem: any}) => true
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const inputId = guid()
            const format = ref('')

            const publicData = { inputId, format }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const renderValue = computed(() => props.cellProps.dataItem[props.cellProps.field])
            const column = computed<GridColumnProps | undefined>(() => props.columns.find(c => c.field == props.cellProps.dataItem.field))
            const columnCell = computed(() => column.value?.cell)
            const renderCellComponent = computed(() => getRenderCellComponent())
            
            const renderArrow = computed(() => {
                const returnValue =
                    props.cellProps.columnIndex === undefined ||
                    props.cellProps.level === undefined ||
                    props.cellProps.columnIndex < props.cellProps.level ||
                    props.cellProps.columnsCount === undefined ||
                    props.cellProps.rowType !== 'groupHeader'

                return returnValue && renderValue.value
            })

            const renderCell = computed(() => props.cellProps.field !== undefined && props.cellProps.rowType !== 'groupHeader')

            const computedData = { renderValue, column, columnCell, renderCellComponent, renderArrow, renderCell }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            function handleOnChange(event: CheckboxChangeEvent) {
                emit('selectionchange', {
                    event: event,
                    dataItem: props.cellProps.dataItem,
                })
            }

            function onClick(e: MouseEvent) {
                emit('click', e, props.cellProps.dataItem, props.cellProps.expanded)
            }

            function getNestedValue(fieldName: string, dataItem: any) {
                const path = fieldName.split('.')

                let data = dataItem
                path.forEach((p) => {
                    data = data ? data[p] : undefined
                })

                return data
            }

            const publicMethods = { handleOnChange, onClick, getNestedValue }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getRenderCellComponent(): (Component | undefined) {
                const columnCellValue = columnCell.value

                if (!columnCellValue) return undefined

                // Cell defined as render function?
                if (typeof columnCellValue === 'function') {
                    return columnCellValue(h, null, props.cellProps)
                }

                // Cell defined as component?
                if (typeof columnCellValue.render === 'function') {
                    return columnCellValue
                }

                // Cell defined as default template?
                switch (columnCellValue) {
                    case 'dateTime':
                        format.value = 'datetime'
                        return DateField

                    case 'date':
                        format.value = 'date'
                        return DateField


                    case 'time':
                        format.value = 'time'
                        return DateField

                    case 'coloredYesNo':
                        format.value = 'coloredYesNo'
                        return BoolField

                    case 'yesNo':
                        format.value = 'yesNo'
                        return BoolField

                    case 'X':
                        format.value = 'X'
                        return BoolField
                }
            }

            /********************************************************************************************************************
             LIFECYCLE HOOKS
             ********************************************************************************************************************/

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
